<template>
  <section class="statistics-card">
    <div class="card-header">
      <h3 class="card-title">
        {{ t('workbench.dataStats.serviceMemberConversion') }}
      </h3>
      <div class="overall-rate">
        {{ t('workbench.dataStats.memberOverallRate') }}
        <strong>{{ summary.overallRate.toFixed(2) }}%</strong>
      </div>
    </div>

    <div class="conversion-grid">
      <article
        v-for="item in conversionItems"
        :key="item.code"
        class="conversion-item"
      >
        <div class="conversion-ring">
          <svg
            class="conversion-ring-svg"
            viewBox="0 0 100 100"
            aria-hidden="true"
          >
            <circle
              class="conversion-ring-track"
              cx="50"
              cy="50"
              r="40"
            />
            <circle
              class="conversion-ring-value"
              cx="50"
              cy="50"
              r="40"
              pathLength="100"
              :stroke="item.color"
              :stroke-dasharray="`${item.rate} ${100 - item.rate}`"
            />
          </svg>
          <div class="conversion-ring-inner">
            <strong>{{ item.rate.toFixed(2) }}%</strong>
            <span>{{ t('workbench.dataStats.conversionRate') }}</span>
          </div>
        </div>

        <div class="conversion-detail">
          <div class="detail-heading">
            <strong>{{ item.name }}</strong>
            <span>{{ item.total }} {{ t('workbench.dataStats.unitGe') }}</span>
          </div>
          <div class="detail-row">
            <span>
              <i :style="{ backgroundColor: item.color }" />
              {{ t('workbench.dataStats.converted') }}
            </span>
            <strong>{{ item.converted }} {{ t('workbench.dataStats.unitGe') }}</strong>
          </div>
          <div class="detail-row">
            <span>
              <i class="is-muted" />
              {{ t('workbench.dataStats.unconverted') }}
            </span>
            <strong>{{ item.unconverted }} {{ t('workbench.dataStats.unitGe') }}</strong>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getServiceMemberConversionStatistics } from '@/api/primaryChannelStatistics'
import { ChannelIdentityTypeEnum } from '@/enums/ChannelTypeEnum'

const { t, locale } = useI18n()

const CONVERSION_DEFS = [
  {
    code: ChannelIdentityTypeEnum.CITY_ASSOC.code,
    nameKey: 'workbench.dataStats.identityCityAssoc',
    color: '#6B4FF7'
  },
  {
    code: ChannelIdentityTypeEnum.ART_NEW_ORG.code,
    nameKey: 'workbench.dataStats.identityArtNewOrg',
    color: '#FFC51B'
  },
  {
    code: ChannelIdentityTypeEnum.DISTRICT_ASSOC.code,
    nameKey: 'workbench.dataStats.identityDistrictAssoc',
    color: '#1478F2'
  },
  {
    code: ChannelIdentityTypeEnum.SUPPORT_REGION.code,
    nameKey: 'workbench.dataStats.identitySupportRegion',
    color: '#16C68B'
  }
]

const conversionDefinitions = computed(() => CONVERSION_DEFS.map(item => ({
  ...item,
  name: `${t(item.nameKey)}${t('workbench.dataStats.serviceMemberTotal')}`
})))

const summary = shallowRef(buildDefaultSummary())
const conversionItems = shallowRef([])

function buildDefaultItems() {
  return conversionDefinitions.value.map(item => ({
    ...item,
    total: 0,
    converted: 0,
    unconverted: 0,
    rate: 0
  }))
}

function buildDefaultSummary() {
  return {
    total: 0,
    convertedTotal: 0,
    unconvertedTotal: 0,
    overallRate: 0
  }
}

function toNumber(value) {
  const number = Number(String(value ?? '').replace('%', ''))
  return Number.isFinite(number) ? number : 0
}

function normalizeStatistics(payload = {}) {
  const sourceItems = Array.isArray(payload.items) ? payload.items : []
  summary.value = {
    total: toNumber(payload.total),
    convertedTotal: toNumber(payload.convertedTotal),
    unconvertedTotal: toNumber(payload.unconvertedTotal),
    overallRate: Math.min(Math.max(toNumber(payload.overallConversionRate), 0), 100)
  }
  conversionItems.value = conversionDefinitions.value.map((definition) => {
    const source = sourceItems.find(item => item.code === definition.code) || {}
    return {
      ...definition,
      total: toNumber(source.total),
      converted: toNumber(source.convertedTotal),
      unconverted: toNumber(source.unconvertedTotal),
      rate: Math.min(Math.max(toNumber(source.conversionRate), 0), 100)
    }
  })
}

async function loadStatistics() {
  try {
    const response = await getServiceMemberConversionStatistics()
    normalizeStatistics(response && response.code === 200 ? response.data : {})
  } catch (error) {
    console.error('查询一级渠道服务会员转化统计失败:', error)
    normalizeStatistics()
  }
}

watch(locale, () => {
  loadStatistics()
})

onMounted(() => {
  conversionItems.value = buildDefaultItems()
  loadStatistics()
})
</script>

<style lang="scss" scoped>
.statistics-card {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 22px 24px 28px;
  border: 1px solid #ebeef5;
  border-radius: 2px;
  background: #fff;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.card-title {
  margin: 0;
  color: #333;
  font-size: 18px;
  font-weight: bold;
  line-height: 1.4;
}

.overall-rate {
  color: #333;
  font-size: 14px;
  white-space: nowrap;

  strong {
    margin-left: 8px;
    font-size: 16px;
  }
}

.conversion-grid {
  display: grid;
  margin-top: 32px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 48px;
  row-gap: 36px;
}

.conversion-item {
  display: grid;
  min-width: 0;
  grid-template-columns: 190px minmax(0, 1fr);
  align-items: center;
  gap: 24px;
}

.conversion-ring {
  position: relative;
  width: 168px;
  height: 168px;
  margin: 0 auto;
  border-radius: 50%;
}

.conversion-ring-svg {
  display: block;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.conversion-ring-track,
.conversion-ring-value {
  fill: none;
  stroke-width: 18;
}

.conversion-ring-track {
  stroke: #f0f2f7;
}

.conversion-ring-value {
  stroke-linecap: butt;
}

.conversion-ring-inner {
  position: absolute;
  inset: 31px;
  display: flex;
  border-radius: 50%;
  background: #fff;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  strong {
    color: #222;
    font-size: 21px;
    line-height: 1.2;
  }

  span {
    margin-top: 5px;
    color: #999;
    font-size: 12px;
  }
}

.conversion-detail {
  min-width: 0;
}

.detail-heading {
  display: grid;
  min-height: 54px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 18px;

  strong {
    color: #333;
    font-size: 14px;
    line-height: 1.45;
  }

  span {
    color: #333;
    font-size: 14px;
    white-space: nowrap;
  }
}

.detail-row {
  display: flex;
  padding-top: 12px;
  align-items: center;
  justify-content: space-between;
  color: #333;
  font-size: 13px;

  span {
    display: inline-flex;
    align-items: center;
  }

  i {
    width: 10px;
    height: 10px;
    margin-right: 9px;
    border-radius: 50%;

    &.is-muted {
      background: #f0f2f7;
    }
  }
}

@media (max-width: 1100px) {
  .conversion-grid {
    column-gap: 24px;
  }

  .conversion-item {
    grid-template-columns: 145px minmax(0, 1fr);
    gap: 12px;
  }

  .conversion-ring {
    width: 136px;
    height: 136px;
  }

  .conversion-ring-inner {
    inset: 25px;

    strong {
      font-size: 17px;
    }
  }
}

@media (max-width: 768px) {
  .statistics-card {
    padding: 16px;
  }

  .card-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .card-title {
    font-size: 16px;
  }

  .overall-rate {
    white-space: normal;
  }

  .conversion-grid {
    grid-template-columns: 1fr;
    row-gap: 28px;
  }

  .conversion-item {
    grid-template-columns: 140px minmax(0, 1fr);
  }
}

@media (max-width: 480px) {
  .statistics-card {
    padding: 12px;
  }

  .card-title {
    font-size: 15px;
  }

  .conversion-item {
    grid-template-columns: 110px minmax(0, 1fr);
    gap: 12px;
  }

  .conversion-ring {
    width: 104px;
    height: 104px;
  }

  .conversion-ring-inner {
    inset: 19px;

    strong {
      font-size: 15px;
    }
  }

  .detail-heading {
    gap: 8px;

    strong,
    span {
      font-size: 12px;
    }
  }
}
</style>
